import React, { useState, useEffect, useMemo } from 'react';
import { StyleSheet, ScrollView } from 'react-native';
import { DNABox, DNAText, DNAButton, DNADivider } from '@alucio/lux-ui';
import { CheckBox } from '@ui-kitten/components';
import { FeatureFlags, FeatureFlagKeys, FlagCategory } from 'src/types/featureFlags';
import useFeatureFlags from 'src/hooks/useFeatureFlags/useFeatureFlags';
import { S } from '..';

const LOCAL_STORAGE_KEY = 'debug_feature_flags_override';

export type FeatureFlagOverrides = Partial<Record<FeatureFlagKeys, boolean | string | number>>;

export const getFeatureFlagOverrides = (): FeatureFlagOverrides => {
  try {
    const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

export const setFeatureFlagOverride = (key: FeatureFlagKeys, value: boolean | string | number | null): void => {
  const overrides = getFeatureFlagOverrides();
  if (value === null) {
    delete overrides[key];
  } else {
    overrides[key] = value;
  }
  localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(overrides));
};

export const clearAllFeatureFlagOverrides = (): void => {
  localStorage.removeItem(LOCAL_STORAGE_KEY);
};

const styles = StyleSheet.create({
  container: {
    maxHeight: 600,
  },
  row: {
    paddingVertical: 8,
    paddingHorizontal: 4,
  },
  flagName: {
    fontSize: 13,
    fontWeight: '600',
  },
  flagDetails: {
    fontSize: 11,
    color: '#666',
  },
  categoryBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    marginRight: 8,
  },
  featureBadge: {
    backgroundColor: '#e3f2fd',
  },
  userPermissionBadge: {
    backgroundColor: '#fff3e0',
  },
  overrideBadge: {
    backgroundColor: '#ffebee',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  header: {
    marginBottom: 16,
  },
});

const FeatureFlagsTab: React.FC = () => {
  const [overrides, setOverrides] = useState<FeatureFlagOverrides>({});
  const [filter, setFilter] = useState<'all' | 'feature' | 'userPermission'>('all');
  const currentFlags = useFeatureFlags();

  useEffect(() => {
    setOverrides(getFeatureFlagOverrides());
  }, []);

  const handleToggle = (key: FeatureFlagKeys, currentValue: boolean) => {
    const newValue = !currentValue;
    setFeatureFlagOverride(key, newValue);
    setOverrides(getFeatureFlagOverrides());
  };

  const handleClearOverride = (key: FeatureFlagKeys) => {
    setFeatureFlagOverride(key, null);
    setOverrides(getFeatureFlagOverrides());
  };

  const handleClearAll = () => {
    clearAllFeatureFlagOverrides();
    setOverrides({});
  };

  const flagEntries = useMemo(() => {
    const entries = Object.entries(FeatureFlags) as [FeatureFlagKeys, typeof FeatureFlags[FeatureFlagKeys]][];

    return entries
      .filter(([_, config]) => {
        if (filter === 'all') return true;
        const category = config.CATEGORY?.[0];
        if (filter === 'feature') return category === FlagCategory.FEATURE;
        if (filter === 'userPermission') return category === FlagCategory.USER_PERMISSION;
        return true;
      })
      .sort((a, b) => a[0].localeCompare(b[0]));
  }, [filter]);

  const overrideCount = Object.keys(overrides).length;

  return (
    <DNABox fill appearance="col" style={S.tabContainer}>
      {/* Header */}
      <DNABox appearance="col" spacing="sm" style={styles.header}>
        <DNAText bold>Feature Flags Debug</DNAText>
        <DNAText style={styles.flagDetails}>
          Override feature flags for debugging. Overrides are stored in localStorage.
        </DNAText>

        {/* Filter buttons */}
        <DNABox spacing="sm">
          <DNAButton
            size="sm"
            appearance={filter === 'all' ? 'filled' : 'outline'}
            onPress={() => setFilter('all')}
          >
            All ({Object.keys(FeatureFlags).length})
          </DNAButton>
          <DNAButton
            size="sm"
            appearance={filter === 'userPermission' ? 'filled' : 'outline'}
            onPress={() => setFilter('userPermission')}
          >
            User Permission
          </DNAButton>
          <DNAButton
            size="sm"
            appearance={filter === 'feature' ? 'filled' : 'outline'}
            onPress={() => setFilter('feature')}
          >
            Feature
          </DNAButton>
        </DNABox>

        {/* Clear all button */}
        {overrideCount > 0 && (
          <DNABox>
            <DNAButton
              size="sm"
              status="danger"
              appearance="outline"
              onPress={handleClearAll}
            >
              Clear All Overrides ({overrideCount})
            </DNAButton>
          </DNABox>
        )}
      </DNABox>

      <DNADivider />

      {/* Flag list */}
      <ScrollView style={styles.container}>
        {flagEntries.map(([key, config]) => {
          const isBoolean = typeof config.defaultValue === 'boolean';
          const hasOverride = key in overrides;
          const currentValue = currentFlags[key];
          const overrideValue = overrides[key];
          const category = config.CATEGORY?.[0];

          return (
            <DNABox key={key} appearance="col" style={styles.row}>
              <DNABox alignY="center" spacing="sm">
                {/* Category badge */}
                {category && (
                  <DNABox
                    style={[
                      styles.categoryBadge,
                      category === FlagCategory.FEATURE ? styles.featureBadge : styles.userPermissionBadge,
                    ]}
                  >
                    <DNAText style={{ fontSize: 10 }}>
                      {category === FlagCategory.USER_PERMISSION ? 'USER' : 'FEAT'}
                    </DNAText>
                  </DNABox>
                )}

                {/* Checkbox for boolean flags */}
                {isBoolean && (
                  <CheckBox
                    checked={hasOverride ? Boolean(overrideValue) : Boolean(currentValue)}
                    onChange={() => handleToggle(key, hasOverride ? Boolean(overrideValue) : Boolean(currentValue))}
                  />
                )}

                {/* Flag name */}
                <DNABox fill appearance="col">
                  <DNAText style={styles.flagName}>{key}</DNAText>
                  <DNAText style={styles.flagDetails}>
                    LD: {config.flag} | Default: {String(config.defaultValue)} | Current: {String(currentValue)}
                  </DNAText>
                </DNABox>

                {/* Override indicator */}
                {hasOverride && (
                  <DNABox alignY="center" spacing="sm">
                    <DNABox style={styles.overrideBadge}>
                      <DNAText style={{ fontSize: 10, color: '#c62828' }}>
                        OVERRIDE: {String(overrideValue)}
                      </DNAText>
                    </DNABox>
                    <DNAButton
                      size="sm"
                      appearance="ghost"
                      status="danger"
                      onPress={() => handleClearOverride(key)}
                    >
                      Clear
                    </DNAButton>
                  </DNABox>
                )}
              </DNABox>
              <DNADivider />
            </DNABox>
          );
        })}
      </ScrollView>
    </DNABox>
  );
};

export default FeatureFlagsTab;
